import { createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import { LocalStorage } from '@service/LocalStorage';
import { roundFloatNumber } from '@utils/helper/roundFloatNumber';
import { FlatId } from '@utils/models/Flat';
import { PriceCalculateType } from '@utils/models/PriceCalculateType';

interface ShaxmatkaState {
  data: any[];
  dataDelete: number[];
  isLoading: boolean;
  priceCalculateType: keyof typeof PriceCalculateType;
  tableMode: boolean;
  changeData: Record<number, FlatId>;
  changeDataRequirets: Record<number, string[]>;
  isChangeData?: boolean;
  isChangeDataValidate?: boolean;
  isConfirmedSoldAndBookedChangedParameters?: boolean;
}

const initialState: ShaxmatkaState = {
  data: [],
  dataDelete: [],
  isLoading: false,
  priceCalculateType: 'PER_AREA',
  tableMode: !!LocalStorage.get('shaxmatkaTableMode'),
  changeData: {},
  isChangeData: false,
  isChangeDataValidate: false,
  changeDataRequirets: {},
  isConfirmedSoldAndBookedChangedParameters: false
};

export const shaxmatkaSlice = createSlice({
  name: 'shaxmatka',
  initialState,
  reducers: {
    setShaxmatka: (state, action: any) => {
      state.data = action.payload;
    },
    setShaxmatkaDelete: (state, action: any) => {
      state.dataDelete = action.payload;
    },
    setShaxmatkaLoading: (state, action: any) => {
      state.isLoading = action.payload;
    },
    setPriceCalculateType: (state, action: PayloadAction<PriceCalculateType | undefined>) => {
      state.priceCalculateType = action.payload || 'PER_AREA';
      const { data } = current(state);
      const arr: FlatId[] = [];

      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      document.querySelectorAll('.selectableItems') &&
        document.querySelectorAll('.selectableItems').forEach(value => {
          const flat = JSON.parse(value.getAttribute('data')!);

          arr.push(flat);
        });
      const shaxmatkaCopy = data.map((podezd: any) =>
        podezd.map((floor: any) =>
          floor.map((flat: FlatId) => {
            function Selected(a: FlatId) {
              return a && a.floor === flat.floor && a.xaxis[0] === flat.xaxis[0] && a.entrance === flat.entrance;
            }

            return arr && arr.some((flat: FlatId) => Selected(flat))
              ? {
                  ...arr.find((flat: FlatId) => Selected(flat)),
                  pricePerArea: !action.payload ? flat?.pricePerArea : flat?.priceNotRepaired! / flat?.area,
                  priceNotRepaired: !action.payload ? flat?.pricePerArea! * flat?.area : flat?.priceNotRepaired,
                  pricePerAreaRepaired: !action.payload
                    ? flat?.pricePerAreaRepaired
                    : flat?.priceRepaired! / flat?.area,
                  priceRepaired: !action.payload ? flat?.pricePerAreaRepaired! * flat?.area : flat?.priceRepaired
                }
              : {
                  ...flat
                };
          })
        )
      );

      state.data = shaxmatkaCopy;
    },
    setTableMode: (state, action: PayloadAction<boolean>) => {
      state.tableMode = action.payload;
      LocalStorage.set('shaxmatkaTableMode', action.payload ? 'true' : '');
    },
    setChangeData: (state, action: PayloadAction<{ key: string; value: any; record: FlatId }>) => {
      const { record, key, value } = action.payload;

      const obj = { ...JSON.parse(JSON.stringify(record)), ...state?.changeData[record?.id] } as FlatId;

      const setValue = {
        'terrace.area': () => {
          obj.terrace.area = value;
        },
        'terrace.pricePerAreaRepaired': () => {
          obj.terrace.pricePerAreaRepaired = value;
        },
        'terrace.pricePerArea': () => {
          obj.terrace.pricePerArea = value;
        }
      };

      if (key in obj) {
        switch (key) {
          case 'area':
            obj.priceRepaired = roundFloatNumber(value * obj.pricePerAreaRepaired);
            obj.priceNotRepaired = roundFloatNumber(value * obj.pricePerArea);
            obj.area = value;
            break;
          case 'pricePerArea':
            obj.priceNotRepaired = roundFloatNumber(value * obj.area);
            obj.pricePerArea = value;
            break;
          case 'pricePerAreaRepaired':
            obj.priceRepaired = roundFloatNumber(value * obj.area);
            obj.pricePerAreaRepaired = value;
            break;
          case 'priceNotRepaired':
            obj.priceNotRepaired = value;
            obj.pricePerArea = roundFloatNumber(value / obj.area);
            break;
          case 'priceRepaired':
            obj.priceRepaired = value;
            obj.pricePerAreaRepaired = roundFloatNumber(value / obj.area);
            break;
          default:
            // @ts-expect-error
            obj[key] = value;
        }
      } else {
        if (!obj?.terrace) {
          obj.terrace = {
            area: undefined,
            pricePerAreaRepaired: undefined,
            pricePerArea: undefined
          };
        }

        setValue[key as keyof typeof setValue]();
      }

      const getObjectValues = (obj: any) =>
        Object.values(obj).reduce((p, c) => {
          if (c) {
            if (typeof c === 'object') {
              p = `${p}${getObjectValues(c)}`;
            } else {
              p = `${p}${c}`;
            }
          }

          return p;
        }, '');

      if (getObjectValues(record || {}) === getObjectValues(obj || {})) {
        delete state.changeData[record?.id];
        delete state.changeDataRequirets[record?.id];
        if (Object.keys(state.changeData).length === 0) {
          state.isChangeData = false;
        }
        if (Object.keys(state.changeDataRequirets).length === 0) {
          state.isChangeDataValidate = false;
        }
      } else {
        const isTeracce =
          (obj.terrace?.area && (obj.terrace?.pricePerArea || obj.terrace?.pricePerAreaRepaired)) ||
          (!obj.terrace?.area && !obj.terrace?.pricePerArea && !obj.terrace?.pricePerAreaRepaired);

        if (obj?.terrace && !isTeracce) {
          state.changeDataRequirets[record?.id] = [
            'terrace.area',
            'terrace.pricePerArea',
            'terrace.pricePerAreaRepaired'
          ];
          state.isChangeDataValidate = true;
        } else {
          delete state.changeDataRequirets[record?.id];
          if (Object.keys(state.changeDataRequirets).length === 0) {
            state.isChangeDataValidate = false;
          }
        }

        state.isChangeData = true;
        state.changeData[record?.id] = obj;
      }
    },
    setIsConfirmedSoldAndBookedChangedParameters: (state, action: PayloadAction<boolean>) => {
      state.isConfirmedSoldAndBookedChangedParameters = action.payload;
    },
    changeDataClear: state => {
      state.changeData = {};
      state.changeDataRequirets = {};
      state.isChangeData = false;
      state.isChangeDataValidate = false;
    }
  }
});

export const {
  setShaxmatka,
  setShaxmatkaDelete,
  setShaxmatkaLoading,
  setPriceCalculateType,
  setTableMode,
  setChangeData,
  changeDataClear,
  setIsConfirmedSoldAndBookedChangedParameters
} = shaxmatkaSlice.actions;
export default shaxmatkaSlice.reducer;
